//import CryptoJS from 'crypto-js';
import {
    BUSINESS_STATUS_ENUM,
    BUSINESS_DOCUMENT_TYPE,
    BUSINESS_STATUS_PIPELINE_BY_DOCUMENT_TYPE,
    getBusinessStatusTransitions
} from "@/models/businessStatusEnum";
import {BUSINESS_DOCUMENT_TYPE_ENUM} from "@/models/businessDocumentTypeEnum";
import {CREDIT_NOTE_STATUS_ENUM} from "@/models/creditNoteStatusEnum";
import {isDepositAborted} from "@/models/depositStatusEnum";
import {PAYMENT_PURPOSE} from "@/models/paymentPurpose";

/**
 * Share common functions
 */

export function roundDownTo2Decimals(value) {
    return Math.floor(parseFloat(value) * 100) / 100;
}

export function debounce (fn, delay, timeoutID) {
    clearTimeout(timeoutID)
    const args = arguments;
    const that = this;
    return setTimeout(function () {
        fn.apply(that, args)
    }, delay)
}

// Crypto part
//const ivKey = process.env.VUE_APP_CRYPTO_IV_KEY;

export function encrypteData(data) {
    /*if(!localStorage.encryptionKey){
        console.log("No encryption key");
        throw Error("No encryption key");
    }
    if (data) {
        const key = CryptoJS.PBKDF2(localStorage.encryptionKey, 'salt', { keySize: 256/32, iterations: 100 });
        const iv = CryptoJS.enc.Utf8.parse(ivKey); // Convert string to WordArray
        const encrypted = CryptoJS.AES.encrypt(data, key, { iv: iv, mode: CryptoJS.mode.CBC });
        return encrypted.ciphertext.toString(CryptoJS.enc.Hex);
    }else{
        return data;
    }*/
    return data;
}

export function convertISOToDate(isoString) {
    const date = new Date(isoString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0'); // Les mois sont de 0 à 11
    const day = String(date.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
}

export function decrypteData(data) {
    /*if(!localStorage.encryptionKey){
        console.log("No encryption key");
        throw Error;
    }
    if (data) {
        const key = CryptoJS.PBKDF2(localStorage.encryptionKey, 'salt', { keySize: 256/32, iterations: 100 });
        const iv = CryptoJS.enc.Utf8.parse(ivKey);
        const decrypted = CryptoJS.AES.decrypt({ ciphertext: CryptoJS.enc.Hex.parse(data) }, key, { iv: iv, mode: CryptoJS.mode.CBC });
        return decrypted.toString(CryptoJS.enc.Utf8);
    }else{
        return data;
    }*/
    return data;
}

export function isEditableBusiness(business){
    return business && !(!!business.invoiceNumber
        || business.status === BUSINESS_STATUS_ENUM.DONE.value
        || business.status === BUSINESS_STATUS_ENUM.SENT.value
        || business.status === BUSINESS_STATUS_ENUM.ACCEPTED.value
        || business.status === BUSINESS_STATUS_ENUM.READY_TO_SEND.value
        || business.status === BUSINESS_STATUS_ENUM.REFUSED.value
        || business.status === BUSINESS_STATUS_ENUM.IN_PROGRESS.value);
}

function findCreditNote(business, document) {
    return (business.creditNotes || []).find(creditNote => creditNote.number === document.creditNoteNumber);
}

/**
 * Est-ce que ce document possède actuellement une valeur comptable (il est "figé") ?
 * `document` a la forme de selectedDocument : {type: BUSINESS_DOCUMENT_TYPE_ENUM.xxx.value, creditNoteNumber}
 *  - devis / facture : à partir de l'envoi (SENT) dans l'enchaînement de statuts de leur type de document.
 *    REFUSED / ABORTED n'y figurent pas : pas de valeur comptable. Le devis d'un business déjà facturé
 *    est DONE (cf. getBusinessStatusTransitions), donc comptable.
 *  - avoir : ISSUED ou REFUNDED (même valeur, la différence ne concerne que les encaissements)
 *  - acompte : comptable dès qu'il est émis, sauf s'il est annulé
 */
export function isAccountingDocument(business, document) {
    if (!business || !document) return false;

    switch (document.type) {
        case BUSINESS_DOCUMENT_TYPE_ENUM.DEPOSIT.value:
            return !isDepositAborted(business);
        case BUSINESS_DOCUMENT_TYPE_ENUM.CREDIT_NOTE.value: {
            const creditNote = findCreditNote(business, document);
            return !!creditNote && (creditNote.status === CREDIT_NOTE_STATUS_ENUM.ISSUED.value
                || creditNote.status === CREDIT_NOTE_STATUS_ENUM.REFUNDED.value);
        }
        case BUSINESS_DOCUMENT_TYPE_ENUM.QUOTE.value:
        case BUSINESS_DOCUMENT_TYPE_ENUM.INVOICE.value: {
            if (!business.status) return false;
            const documentType = document.type === BUSINESS_DOCUMENT_TYPE_ENUM.QUOTE.value
                ? BUSINESS_DOCUMENT_TYPE.QUOTE
                : BUSINESS_DOCUMENT_TYPE.INVOICE;
            const {savedStatus} = getBusinessStatusTransitions(business, undefined, documentType);
            const pipeline = BUSINESS_STATUS_PIPELINE_BY_DOCUMENT_TYPE[documentType];
            const sentIndex = pipeline.findIndex(status => status.value === BUSINESS_STATUS_ENUM.SENT.value);
            return pipeline.findIndex(status => status.value === savedStatus) >= sentIndex;
        }
        default:
            return false;
    }
}

/**
 * Montant TTC d'un document, qu'il ait ou non une valeur comptable (voir isAccountingDocument pour le savoir) :
 *  - devis : aucun montant (proposition commerciale, pas un flux financier : le total reste visible dans le devis)
 *  - facture : TTC dû, acompte déduit (même règle que l'impression de la facture, prepareInvoiceWithDeposit)
 *  - acompte : TTC de l'acompte
 *  - avoir : TTC de l'avoir en négatif (représentation d'affichage, le modèle n'est pas modifié)
 */
export function getDocumentTtcAmount(business, document) {
    if (!business || !document) return undefined;

    switch (document.type) {
        case BUSINESS_DOCUMENT_TYPE_ENUM.INVOICE.value: {
            const deposit = (business.depositNumber && !isDepositAborted(business)) ? (Number(business.deposit) || 0) : 0;
            return Math.round((business.ttcAmount - deposit) * 100) / 100;
        }
        case BUSINESS_DOCUMENT_TYPE_ENUM.DEPOSIT.value:
            return business.deposit;
        case BUSINESS_DOCUMENT_TYPE_ENUM.CREDIT_NOTE.value: {
            const creditNote = findCreditNote(business, document);
            return creditNote?.body?.ttcAmount !== undefined ? 0 - creditNote.body.ttcAmount : undefined;
        }
        default:
            return undefined;
    }
}

/**
 * Document concerné par un encaissement (paymentPurpose, optionnel) : acompte ou facture.
 * undefined si non précisé ou non reconnu : l'encaissement n'est alors rattaché à aucun document en particulier.
 */
function getCollectionDocumentType(collection) {
    const purpose = String(collection.paymentPurpose || '').toUpperCase();
    if (purpose.includes('DEPOSIT')) return BUSINESS_DOCUMENT_TYPE_ENUM.DEPOSIT.value;
    if (purpose.includes('INVOICE')) return BUSINESS_DOCUMENT_TYPE_ENUM.INVOICE.value;
    return undefined;
}

/**
 * Encaissement à proposer pour passer un document à payé (acompte, facture) ou remboursé (avoir) : ce qui reste à encaisser
 * sur ce document (les encaissements existants sont comptés par motif). Même règle que le passage automatique de ms-business.
 * Retourne {amount, paymentPurpose, creditNoteNumber?} ou undefined s'il n'y a plus rien à encaisser.
 * Le remboursement d'un avoir est un montant négatif.
 * document : {type: BUSINESS_DOCUMENT_TYPE_ENUM.xxx.value, creditNoteNumber}
 */
export function getCollectionProposal(business, collections, document) {
    if (!business || !document) return undefined;

    const round2 = value => Math.round(value * 100) / 100;

    switch (document.type) {
        case BUSINESS_DOCUMENT_TYPE_ENUM.DEPOSIT.value:
        case BUSINESS_DOCUMENT_TYPE_ENUM.INVOICE.value: {
            // Reste à encaisser du document, tel que l'affiche la synthèse financière (encaissements déjà reçus déduits :
            // ceux qui visent ce document, et ceux sans motif précis qui comblent les factures émises dans l'ordre)
            const summaryDocument = getBusinessFinancialSummary(business, collections).documents
                .find(current => current.type === document.type && current.isAccounting);
            const amount = summaryDocument ? round2(summaryDocument.remaining) : 0;
            return amount > 0
                ? {amount, paymentPurpose: document.type === BUSINESS_DOCUMENT_TYPE_ENUM.DEPOSIT.value ? PAYMENT_PURPOSE.DEPOSIT.value : PAYMENT_PURPOSE.INVOICE.value}
                : undefined;
        }
        case BUSINESS_DOCUMENT_TYPE_ENUM.CREDIT_NOTE.value: {
            const creditNote = findCreditNote(business, document);
            const ttcAmount = Number(creditNote && creditNote.body && creditNote.body.ttcAmount) || 0;
            const refunded = Math.abs((collections || [])
                .filter(collection => collection.paymentPurpose === PAYMENT_PURPOSE.CREDIT_NOTE.value && collection.creditNoteNumber === document.creditNoteNumber)
                .reduce((total, collection) => total + (Number(collection.amount) || 0), 0));
            const amount = round2(ttcAmount - refunded);
            return amount > 0
                ? {amount: -amount, paymentPurpose: PAYMENT_PURPOSE.CREDIT_NOTE.value, creditNoteNumber: document.creditNoteNumber}
                : undefined;
        }
        default:
            return undefined;
    }
}

/**
 * Répartit les encaissements du business sur les factures émises (acompte puis facture) pour savoir ce qui reste dû sur chacune.
 * Ajoute à chaque document concerné {due, paid, remaining} :
 *  - due       : acompte = montant de l'acompte ; facture = TTC net de l'acompte (cf. getDocumentTtcAmount), moins les avoirs comptables
 *  - un encaissement qui précise son document est affecté à celui-ci (le surplus éventuel est réparti comme un encaissement non précisé)
 *  - un encaissement non précisé, ou le surplus, comble les factures émises dans l'ordre : acompte puis facture
 *  - un encaissement visant un document pas encore émis n'est affecté à aucune facture
 * Les avoirs ne sont jamais des encaissements.
 */
function allocateCollectionsToDocuments(documents, collections) {
    const round2 = value => Math.round(value * 100) / 100;
    const creditNotesTotal = documents.filter(d => d.type === BUSINESS_DOCUMENT_TYPE_ENUM.CREDIT_NOTE.value && d.isAccounting)
        .reduce((sum, d) => sum + d.amount, 0);
    const payables = [BUSINESS_DOCUMENT_TYPE_ENUM.DEPOSIT.value, BUSINESS_DOCUMENT_TYPE_ENUM.INVOICE.value]
        .map(type => documents.find(d => d.type === type && d.isAccounting))
        .filter(Boolean);

    payables.forEach(document => {
        document.due = Math.max(0, round2(document.type === BUSINESS_DOCUMENT_TYPE_ENUM.INVOICE.value ? document.amount + creditNotesTotal : document.amount));
        document.paid = 0;
    });

    // Retourne ce qui reste de l'encaissement une fois affecté au document
    const apply = (document, amount) => {
        const applied = Math.min(amount, document.due - document.paid);
        document.paid = round2(document.paid + applied);
        return round2(amount - applied);
    };

    const pool = [];
    [...(collections || [])]
        .filter(collection => Number(collection.amount) > 0)
        .sort((a, b) => new Date(a.date) - new Date(b.date))
        .forEach(collection => {
            // Un encaissement sur un avoir (remboursement) ne règle ni l'acompte ni la facture
            if (collection.paymentPurpose === PAYMENT_PURPOSE.CREDIT_NOTE.value) return;
            let amount = Number(collection.amount);
            const type = getCollectionDocumentType(collection);
            if (type) {
                const document = payables.find(d => d.type === type);
                if (!document) return;
                amount = apply(document, amount);
            }
            if (amount > 0) pool.push(amount);
        });
    pool.forEach(amount => payables.forEach(document => {
        amount = apply(document, amount);
    }));

    payables.forEach(document => {
        document.remaining = round2(document.due - document.paid);
    });
}

/**
 * Synthèse financière d'un business (montants TTC) :
 *   documents comptables -> montant demandé -> encaissements du business -> solde
 *  - requested          : somme des documents ayant une valeur comptable (cf. isAccountingDocument) :
 *                         acompte + facture émise - avoirs ISSUED / REFUNDED
 *  - theoretical        : total attendu du contrat, c'est-à-dire ce qui aura été demandé une fois tous les documents
 *                         attendus émis (acompte + facture finale même non envoyée, avoirs comptables déduits).
 *                         Simple indicateur : aucun document n'est créé ni modifié.
 *  - pending            : theoretical - requested (> 0 : reste à demander, < 0 : documents émis au-delà du contrat)
 *  - collected          : somme des encaissements. Ils concernent le business dans son ensemble (paymentPurpose, optionnel,
 *                         précise seulement le document concerné) et ne contiennent jamais d'avoir.
 *  - balance            : collected - requested (> 0 : encaissé en trop, < 0 : reste à encaisser)
 *  - theoreticalBalance : collected - theoretical
 *  - documents          : détail [{type, creditNoteNumber, amount, isAccounting, isExpected}] des documents pris en compte ;
 *                         les factures émises (acompte, facture) portent en plus {due, paid, remaining} (cf. allocateCollectionsToDocuments)
 * collections : [{amount, ...}] encaissements du business
 */
export function getBusinessFinancialSummary(business, collections = []) {
    const round2 = value => Math.round(value * 100) / 100;
    const documents = [];

    const addDocument = (document, isAccounting, isExpected) => {
        const amount = getDocumentTtcAmount(business, document);
        if (amount === undefined) return;
        documents.push({...document, amount: Number(amount) || 0, isAccounting, isExpected});
    };

    if (business) {
        if (business.depositNumber) {
            const deposit = {type: BUSINESS_DOCUMENT_TYPE_ENUM.DEPOSIT.value};
            addDocument(deposit, isAccountingDocument(business, deposit), true);
        }

        // Facture finale : attendue tant que le business n'est ni annulé ni refusé (même si elle n'est pas encore émise)
        const invoice = {type: BUSINESS_DOCUMENT_TYPE_ENUM.INVOICE.value};
        const isDiscontinued = business.status === BUSINESS_STATUS_ENUM.ABORTED.value
            || (!business.invoiceNumber && business.status === BUSINESS_STATUS_ENUM.REFUSED.value);
        addDocument(invoice, !!business.invoiceNumber && isAccountingDocument(business, invoice), !isDiscontinued);

        // Avoirs : seuls ISSUED / REFUNDED comptent (isAccountingDocument), CREATION / ABORTED sont ignorés
        (business.creditNotes || []).forEach(creditNote => {
            const document = {type: BUSINESS_DOCUMENT_TYPE_ENUM.CREDIT_NOTE.value, creditNoteNumber: creditNote.number};
            const isAccounting = isAccountingDocument(business, document);
            addDocument(document, isAccounting, isAccounting);
        });
    }

    allocateCollectionsToDocuments(documents, collections);

    const requested = round2(documents.filter(d => d.isAccounting).reduce((sum, d) => sum + d.amount, 0));
    const theoretical = round2(documents.filter(d => d.isExpected).reduce((sum, d) => sum + d.amount, 0));
    const collected = round2((collections || []).reduce((sum, collection) => sum + (Number(collection.amount) || 0), 0));

    return {
        requested,
        theoretical,
        pending: round2(theoretical - requested),
        collected,
        balance: round2(collected - requested),
        theoreticalBalance: round2(collected - theoretical),
        documents,
    };
}

export function possibleBusinessStatusSelect(business) {
    const values = [];
    const status = business.status;
    const isInvoice = !!business.invoiceNumber;
    const isCreditNote = !!business.creditNoteNumber;
    const isQuote = !isInvoice && !isCreditNote;

    // CREATION
    if(isQuote && (status === BUSINESS_STATUS_ENUM.READY_TO_SEND.value || status === BUSINESS_STATUS_ENUM.SENT.value)) {
        values.push(BUSINESS_STATUS_ENUM.CREATION);
    }

    // SENT on invoice
    if(isInvoice && status === BUSINESS_STATUS_ENUM.READY_TO_SEND.value) {
        values.push(BUSINESS_STATUS_ENUM.SENT);
    }

    // SENT on quote
    if(isQuote && status === BUSINESS_STATUS_ENUM.READY_TO_SEND.value) {
        values.push(BUSINESS_STATUS_ENUM.SENT);
    }

    // READY_TO_SEND
    if(isQuote && status === BUSINESS_STATUS_ENUM.CREATION.value) {
        values.push(BUSINESS_STATUS_ENUM.READY_TO_SEND);
    }

    // ABORTED
    if(status !== BUSINESS_STATUS_ENUM.PAID.value){
        values.push(BUSINESS_STATUS_ENUM.ABORTED);
    }

    // IN_PROGRESS
    if(isQuote && status === BUSINESS_STATUS_ENUM.ACCEPTED.value) {
        values.push(BUSINESS_STATUS_ENUM.IN_PROGRESS);
    }

    // ACCEPTED
    if(isQuote && status === BUSINESS_STATUS_ENUM.SENT.value) {
        // Signature case
        if(business.signature && business.signature.signedAt){
            values.push(BUSINESS_STATUS_ENUM.ACCEPTED);
        }else if(!business.signature){
            values.push(BUSINESS_STATUS_ENUM.ACCEPTED);
        }
    }

    // DONE
    if(isQuote && status === BUSINESS_STATUS_ENUM.IN_PROGRESS.value) {
        values.push(BUSINESS_STATUS_ENUM.DONE);
    }

    // REFUSED
    if(isQuote && status === BUSINESS_STATUS_ENUM.SENT.value) {
        values.push(BUSINESS_STATUS_ENUM.REFUSED);
    }

    // PAID
    if(isInvoice && status === BUSINESS_STATUS_ENUM.SENT.value) {
        values.push(BUSINESS_STATUS_ENUM.PAID);
    }

    // PAID_AND_DECLARED
    if(isInvoice && status === BUSINESS_STATUS_ENUM.PAID.value) {
        values.push(BUSINESS_STATUS_ENUM.PAID_AND_DECLARED);
    }

    // READY_TO_SEND on credit note
    if(isCreditNote && status === BUSINESS_STATUS_ENUM.CREATION.value) {
        values.push(BUSINESS_STATUS_ENUM.READY_TO_SEND);
    }

    // SENT on credit note
    if(isCreditNote && status === BUSINESS_STATUS_ENUM.READY_TO_SEND.value) {
        values.push(BUSINESS_STATUS_ENUM.SENT);
    }

    // ISSUED on credit note
    if(isCreditNote && status === BUSINESS_STATUS_ENUM.SENT.value) {
        values.push(BUSINESS_STATUS_ENUM.ISSUED);
    }

    // REFUNDED on credit note
    if(isCreditNote && status === BUSINESS_STATUS_ENUM.ISSUED.value) {
        values.push(BUSINESS_STATUS_ENUM.REFUNDED);
    }

    // Current status
    values.push(BUSINESS_STATUS_ENUM[status]);

    return values;
}

export function timeAgo(date) {
    const now = new Date()
    const diffInMs = new Date(date) - now // positif si futur, négatif si passé
    const isNegative = diffInMs < 0
    const diffInHours = Math.floor(Math.abs(diffInMs) / (1000 * 60 * 60))
    const diffInDays = Math.floor(diffInHours / 24)

    if (diffInDays >= 1) {
        return `${isNegative ? 'Il y a ' : 'Dans '}${diffInDays} jours`
    } else {
        return `${isNegative ? 'Il y a ' : 'Dans '}${diffInHours}h`
    }
}

export const formattedDateTime = {
    day: "numeric",
    month: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit"
}

export const formattedDate = {
    day: "numeric",
    month: "numeric",
    year: "numeric"
}

export const CLIENT_TYPE_ENUM = {
    FRENCH_COMPANY: { label: 'Entreprise française', value: "FRENCH_COMPANY" },
    INTERNATIONAL_COMPANY: { label: 'Entreprise internationale', value: "INTERNATIONAL_COMPANY" },
    INDIVIDUAL: { label: 'Particulier', value: "INDIVIDUAL" },
    UNKNOWN: { label: 'Inconnu', value: "UNKNOWN" },
}

export const CLIENT_TYPE_ENUM_ARRAY= [
    CLIENT_TYPE_ENUM.FRENCH_COMPANY,
    CLIENT_TYPE_ENUM.INTERNATIONAL_COMPANY,
    CLIENT_TYPE_ENUM.INDIVIDUAL,
    CLIENT_TYPE_ENUM.UNKNOWN
]

// Statut TVA du client (assujettissement), indépendant de son type : il ne modifie jamais les taux de TVA des documents
export const CLIENT_VAT_STATUS_ENUM = {
    UNKNOWN: { label: 'Non renseigné', value: "UNKNOWN" },
    SUBJECT_TO_VAT: { label: 'Assujetti à la TVA', value: "SUBJECT_TO_VAT" },
    NOT_SUBJECT_TO_VAT: { label: 'Non assujetti à la TVA', value: "NOT_SUBJECT_TO_VAT" },
}

export const CLIENT_VAT_STATUS_ENUM_ARRAY = [
    CLIENT_VAT_STATUS_ENUM.UNKNOWN,
    CLIENT_VAT_STATUS_ENUM.SUBJECT_TO_VAT,
    CLIENT_VAT_STATUS_ENUM.NOT_SUBJECT_TO_VAT
]

// Un particulier agit généralement à titre privé : le statut TVA ne lui est pas demandé (le statut déjà enregistré est conservé)
export function isClientVatStatusDisplayed(clientType){
    return clientType !== CLIENT_TYPE_ENUM.INDIVIDUAL.value;
}

// SIRET / SIREN : seuls les chiffres sont conservés (jamais d'espace en base), les espaces ne servent qu'à l'affichage
export const SIRET_MASK = { maxDigits: 14, groups: [3, 3, 3, 5] }
export const SIREN_MASK = { maxDigits: 9, groups: [3, 3, 3] }

export function normalizeIdentifier(value, maxDigits){
    return String(value ?? '').replace(/\D/g, '').slice(0, maxDigits);
}

export function formatIdentifier(value, mask){
    const digits = normalizeIdentifier(value, mask.maxDigits);
    const parts = [];
    let index = 0;
    for(const size of mask.groups){
        if(index >= digits.length){ break }
        parts.push(digits.slice(index, index + size));
        index += size;
    }
    return parts.join(' ');
}

// Le SIREN correspond aux 9 premiers chiffres du SIRET
export function sirenFromSiret(siret){
    const digits = normalizeIdentifier(siret, SIRET_MASK.maxDigits);
    return digits.length >= 9 ? digits.slice(0, 9) : '';
}

export function convertPaymentType(paymentType){
    if(paymentType === "CASH"){ return "Espèce" }
    else if(paymentType === "CHECK"){ return "Chèque" }
    else if(paymentType === "TRANSFER"){ return "Virement" }
    else{ return "-" }
}

export function removePaginationFromURLParams(params) {
    const searchParams = new URLSearchParams(params);
    searchParams.delete("pages");
    searchParams.delete("index");
    return "?" + searchParams.toString();
}